/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import { FiTag, FiPercent, FiBriefcase, FiInfo } from 'react-icons/fi';
import {
  formatPrice,
  derivePlanTitle,
  derivePlanSubtitle,
  formatDataCapacity,
  formatValidity,
} from '../../utils/esimUtils';
import './EsimFareSummary.scss';

function Row({ icon, label, value, accent }) {
  return (
    <li
      className={`esim-fare-summary__row ${
        accent ? 'esim-fare-summary__row--accent' : ''
      }`}
    >
      <span className="esim-fare-summary__row-label">
        {icon ? (
          <span className="esim-fare-summary__row-icon">{icon}</span>
        ) : null}
        {label}
      </span>
      <span className="esim-fare-summary__row-value">{value}</span>
    </li>
  );
}

Row.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  accent: PropTypes.bool,
};

Row.defaultProps = {
  icon: null,
  accent: false,
};

function EsimFareSummary({ plan, currency }) {
  if (!plan) {
    return (
      <aside className="esim-fare-summary esim-fare-summary--empty">
        <h3 className="esim-fare-summary__title">Fare Summary</h3>
        <span className="esim-fare-summary__title-underline" aria-hidden />
        <p className="esim-fare-summary__empty-msg">
          Pick an eSIM plan to see the fare breakdown here.
        </p>
      </aside>
    );
  }

  const cur = currency || plan?.currency || 'INR';
  const baseFare = Number(plan?.baseFare || plan?.netPrice || 0);
  const tax = Number(plan?.tax || 0);
  const handling = Number(plan?.handlingCharges || 0);
  const slashed = Number(plan?.slashedNetPrice || 0);
  const total = Number(plan?.totalPrice || plan?.fareQuoteTotal || 0);
  const showSlashed = slashed > total && total > 0;

  return (
    <aside className="esim-fare-summary">
      <h3 className="esim-fare-summary__title">Fare Summary</h3>
      <span className="esim-fare-summary__title-underline" aria-hidden />

      <div className="esim-fare-summary__plan">
        <span className="esim-fare-summary__plan-eyebrow">Selected Plan</span>
        <strong className="esim-fare-summary__plan-name">
          {derivePlanTitle(plan)}
        </strong>
        {derivePlanSubtitle(plan) && (
          <span className="esim-fare-summary__plan-sub">
            {derivePlanSubtitle(plan)}
          </span>
        )}
        <div className="esim-fare-summary__plan-meta">
          <span>{formatDataCapacity(plan)}</span>
          <span aria-hidden>·</span>
          <span>{formatValidity(plan) || 'Flexible validity'}</span>
        </div>
      </div>

      <ul className="esim-fare-summary__rows">
        <Row
          icon={<FiTag />}
          label="Base fare"
          value={formatPrice(baseFare, cur) || `${cur} ${baseFare}`}
        />
        <Row
          icon={<FiPercent />}
          label="Taxes & Fees"
          value={formatPrice(tax, cur) || `${cur} ${tax}`}
        />
        {handling > 0 && (
          <Row
            icon={<FiBriefcase />}
            label="Handling charges"
            value={formatPrice(handling, cur) || `${cur} ${handling}`}
          />
        )}
      </ul>

      <div className="esim-fare-summary__divider" />

      <div className="esim-fare-summary__total">
        <span>Total Payable</span>
        <span className="esim-fare-summary__total-value">
          {showSlashed && (
            <small>{formatPrice(slashed, cur) || `${cur} ${slashed}`}</small>
          )}
          {formatPrice(total, cur) || `${cur} ${total}`}
        </span>
      </div>

      <p className="esim-fare-summary__note">
        <FiInfo />
        All prices are inclusive of applicable taxes. eSIM activation requires a
        compatible device and a stable data connection at the destination.
      </p>
    </aside>
  );
}

EsimFareSummary.propTypes = {
  plan: PropTypes.object,
  currency: PropTypes.string,
};

EsimFareSummary.defaultProps = {
  plan: null,
  currency: '',
};

export default EsimFareSummary;
